import axios, { AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getApiConfig } from '../environment';

export interface AuthenticatedAxiosConfig extends AxiosRequestConfig {
  includeAuth?: boolean;
}

interface StoredStore {
  storeNumber?: string;
  franchiseId?: number;
}

const getStoredStore = (): StoredStore | null => {
  const selectedStoreValue = localStorage.getItem('selectedStore');
  if (!selectedStoreValue || selectedStoreValue === 'null') return null;

  try {
    return JSON.parse(selectedStoreValue) as StoredStore;
  } catch (error) {
    console.warn('Failed to parse selectedStore from localStorage:', error);
    return null;
  }
};

const getHeaderValue = (headers: AxiosHeaders, name: string) => {
  const value = headers.get(name);
  return typeof value === 'string' ? value.trim() : '';
};

// Explicit store headers are set by callers for store-scoped requests. They must
// remain the source of truth while React state and localStorage are transitioning.
export const applyStoreContext = (headers: AxiosHeaders, storedStore: StoredStore | null) => {
  const explicitStoreNumber = getHeaderValue(headers, 'ews-storeNumber');
  const effectiveStoreNumber = explicitStoreNumber || storedStore?.storeNumber || '';
  const isStoredStoreCurrent = storedStore?.storeNumber === effectiveStoreNumber;

  headers.set('ews-storeNumber', effectiveStoreNumber);
  headers.set('X-Context-Store-Number', effectiveStoreNumber);

  if (isStoredStoreCurrent) {
    headers.set('X-Context-Franchise-Id', storedStore?.franchiseId?.toString() || '');
  } else {
    // Franchise context belongs to the stored store. Do not send it for another
    // explicitly requested store while the selected-store persistence is stale.
    headers.delete('X-Context-Franchise-Id');
  }
};

export const createAuthenticatedAxiosInstance = (
  config: AuthenticatedAxiosConfig = {},
  includeAuth: boolean = true,
): AxiosInstance => {

  const apiConfig = getApiConfig();
  const language = localStorage.getItem('language');

  const defaultConfig: AxiosRequestConfig = {
    baseURL: apiConfig.apiBaseUrl,
    ...config,
  };

  const axiosInstance = axios.create(defaultConfig);

  if (includeAuth) {

    axiosInstance.interceptors.request.use((requestConfig) => {
      const headers = AxiosHeaders.from(requestConfig.headers);
      const storedStore = getStoredStore();

      const storedValue = localStorage.getItem('token');

      const accessToken = storedValue && JSON.parse(storedValue);
      if (accessToken && accessToken !== 'null') {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      applyStoreContext(headers, storedStore);

      headers.set('Accept', 'application/json');
      headers.set('X-Requested-With', 'XMLHttpRequest');
      headers.set('Expires', '-1');
      headers.set('If-Modified-Since', 'Thu, 13 Feb 1985 13:40:01 GMT');
      headers.set('Cache-Control', 'no-cache');
      headers.set('Pragma', 'no-cache');

      if (language) {
        headers.set('Accept-Language', language);
      }

      requestConfig.headers = headers;

      return requestConfig;
    },
    (error) => {
      return Promise.reject(error);
    },
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const { response } = error;
        if (response && response.status === 401) {
          localStorage.clear();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      },
    );

  }

  return axiosInstance;
};


export const createPublicAxiosInstance = (config: AxiosRequestConfig = {}): AxiosInstance => {
  return createAuthenticatedAxiosInstance(config, false);
};

export const authenticatedAxios = createAuthenticatedAxiosInstance();
